import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import LoadingIndicator from './LoadingIndicator';
import { FlexipassDoor } from '../../../models';

import CheckMarkSvg from '../../../public/images/icons/check-mark.svg';

import styles from './DoorState.module.scss';

interface DoorStateProps {
  opening: boolean;
  error: boolean;
  door: FlexipassDoor;
}

export const DoorState = ({
  opening,
  error,
  door,
}: DoorStateProps): React.ReactElement => {
  const intl = useIntl();

  const renderIndicator = useCallback(() => {
    if (error) {
      return (
        <span className={styles.indicator}>
          <ErrorOutlineIcon style={{ fontSize: 70, fill: 'gray' }} />
        </span>
      );
    }

    if (opening) {
      return <LoadingIndicator />;
    }

    return (
      <span className={styles.indicator}>
        <CheckMarkSvg height={60} width={60} />
      </span>
    );
  }, [error, opening]);

  const renderStatus = useCallback(() => {
    if (error) {
      return (
        <span className={styles.status}>
          {intl.formatMessage({
            id: 'digital_keys/error',
            defaultMessage: 'Could not open the door. Please try again.',
          })}
        </span>
      );
    }

    if (opening) {
      return (
        <span className={styles.status}>
          {intl.formatMessage({
            id: 'digital_keys/opening',
            defaultMessage: 'Opening...',
          })}
        </span>
      );
    }

    return (
      <span className={styles.status}>
        {intl.formatMessage({
          id: 'digital_keys/open',
          defaultMessage: 'Open',
        })}
      </span>
    );
  }, [error, intl, opening]);

  return (
    <div className={styles.content}>
      <span className={styles.door}>{door.display_name ?? door.door_name}</span>
      {renderIndicator()}
      {renderStatus()}
    </div>
  );
};
